import {
  ArrowLeft,
  Bell,
  Boxes,
  FileText,
  LayoutDashboard,
  Moon,
  Save,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Observer } from '@/components/observer/Observer';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import {
  useObserver,
  type ObserverEventKind,
  type ObserverMotion,
  type ObserverPresence,
  type ObserverShape,
  type ObserverState,
} from '@/components/observer/observerContext';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import { Button, IconButton, Switch, cn } from '@/components/ui';
import { useTheme } from '@/theme/ThemeProvider';

/*
  /design/observer — площадка «Наблюдателя». Он сидит домом под активным пунктом, перетекает
  по уровням навигации, подлетает к источнику события, обводит его кольцом, делает жест и
  возвращается. Настройки — его собственные, включая полное отключение.
*/

/** Демо-меню: нейтральные разделы, не привязанные к прикладным модулям площадки. */
const sections = [
  { id: 'overview', label: 'Обзор', icon: LayoutDashboard },
  { id: 'services', label: 'Сервисы', icon: Boxes },
  { id: 'docs', label: 'Документы', icon: FileText },
  { id: 'team', label: 'Команда', icon: Users },
  { id: 'settings', label: 'Настройки', icon: Settings },
];

const subsections: Record<string, { id: string; label: string }[]> = {
  services: [
    { id: 'catalog', label: 'Каталог' },
    { id: 'connected', label: 'Подключённые' },
    { id: 'access', label: 'Доступы' },
  ],
  docs: [
    { id: 'guides', label: 'Инструкции' },
    { id: 'templates', label: 'Шаблоны' },
  ],
};

const states: { value: ObserverState; label: string }[] = [
  { value: 'idle', label: 'покой' },
  { value: 'thinking', label: 'думает' },
  { value: 'writing', label: 'пишет' },
  { value: 'download', label: 'качает' },
  { value: 'upload', label: 'отдаёт' },
  { value: 'update', label: 'обновляет' },
  { value: 'success', label: 'готово' },
  { value: 'error', label: 'ошибка' },
];

const events: { kind: ObserverEventKind; label: string; anchorId?: string }[] = [
  { kind: 'notification', label: 'Новое уведомление', anchorId: 'header.bell' },
  { kind: 'message', label: 'Сообщение в чате', anchorId: 'header.bell' },
  { kind: 'error', label: 'Ошибка в документе', anchorId: 'row.document' },
  { kind: 'done', label: 'Операция завершена', anchorId: 'action.save' },
  { kind: 'update', label: 'Фоновое обновление' },
];

const presences: { value: ObserverPresence; label: string }[] = [
  { value: 'quiet', label: 'тихий указатель' },
  { value: 'companion', label: 'компаньон' },
];

const motions: { value: ObserverMotion; label: string }[] = [
  { value: 'restrained', label: 'сдержанно' },
  { value: 'pronounced', label: 'выражено' },
  { value: 'cartoon', label: 'мультяшно' },
];

const shapes: { value: ObserverShape; label: string }[] = [
  { value: 'dock', label: 'подложка' },
  { value: 'dot', label: 'точка' },
  { value: 'planet', label: 'планета' },
];

const eventNames: Partial<Record<ObserverEventKind, string>> = {
  notification: 'уведомления',
  message: 'сообщения',
  error: 'ошибки',
  done: 'завершения',
  update: 'обновления',
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-40 shrink-0 text-2xs font-medium text-fg-subtle uppercase">{label}</span>
      {children}
    </div>
  );
}

function NavButton({
  id,
  label,
  active,
  trace,
  level,
  onClick,
  icon,
}: {
  id: string;
  label: string;
  active: boolean;
  trace: boolean;
  level: number;
  onClick: () => void;
  icon?: ReactNode;
}) {
  const ref = useObserverAnchor<HTMLButtonElement>(id, { role: 'home', label, level });
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex h-9 w-full items-center gap-2 rounded-md px-3 text-sm transition-colors duration-fast',
        // Выделение активного пункта постоянное и своё (утилита nav-active): Наблюдатель
        // больше не занимает меню, он живёт на углах блоков.
        active ? 'nav-active font-medium text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
        trace && 'text-fg',
      )}
    >
      {trace && (
        <span
          className="absolute top-1/2 left-0 h-4 w-0.5 -translate-y-1/2 rounded-full bg-brand"
          aria-hidden
        />
      )}
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

/** Карточка, которая знает о себе: компаньон садится на её верхний правый угол. */
function Block({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  const ref = useObserverAnchor<HTMLElement>(id, { role: 'block', label });
  return (
    <section ref={ref} className="rounded-lg border border-line bg-card p-5">
      {children}
    </section>
  );
}

function Playground() {
  const { mode, toggle } = useTheme();
  const { settings, state, shape, setSettings, setState, setShape, setHome, notify, setOnActivate } =
    useObserver();
  const [section, setSection] = useState('services');
  const [sub, setSub] = useState<string | null>('catalog');
  const [answer, setAnswer] = useState<string | null>(null);

  const bellRef = useObserverAnchor<HTMLButtonElement>('header.bell', {
    role: 'source',
    label: 'Уведомления',
  });
  const saveRef = useObserverAnchor<HTMLButtonElement>('action.save', {
    role: 'action',
    label: 'Сохранить',
  });
  const rowRef = useObserverAnchor<HTMLDivElement>('row.document', {
    role: 'item',
    label: 'Документ «Отчёт за неделю»',
  });

  // Дом — самый глубокий активный уровень навигации.
  useEffect(() => {
    setHome(sub ? `nav.${section}.${sub}` : `nav.${section}`);
  }, [section, sub, setHome]);

  // Задел на помощника: по нажатию объясняет, что на экране.
  useEffect(() => {
    if (!settings.interactive) {
      setOnActivate(null);
      return;
    }
    setOnActivate(() => () => {
      const s = sections.find((x) => x.id === section)?.label ?? '';
      const sb = sub ? (subsections[section]?.find((x) => x.id === sub)?.label ?? '') : '';
      setAnswer(`Открыт раздел «${s}»${sb ? `, подраздел «${sb}»` : ''}. Спросите меня про этот экран.`);
    });
  }, [settings.interactive, section, sub, setOnActivate]);

  const subs = subsections[section] ?? [];

  const toggleReaction = useCallback(
    (kind: ObserverEventKind) => {
      setSettings({ reactions: { ...settings.reactions, [kind]: !settings.reactions[kind] } });
    },
    [setSettings, settings.reactions],
  );

  return (
    <div className="h-screen overflow-y-auto bg-frame text-fg">
      <header className="sticky top-0 z-raised flex items-center gap-4 border-b border-line bg-canvas px-6 py-3">
        <Link
          to="/design"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="size-3.5" />
          Галерея
        </Link>
        <span className="text-sm font-semibold text-fg">Наблюдатель</span>
        <Link to="/design/character" className="text-xs font-medium text-brand-fg hover:underline">
          Характер
        </Link>
        <Link to="/design/observer/lab" className="text-xs font-medium text-brand-fg hover:underline">
          Стенд
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <IconButton ref={bellRef} aria-label="Уведомления" variant="secondary" size="sm">
            <Bell />
          </IconButton>
          <IconButton
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            variant="secondary"
            size="sm"
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl gap-6 px-6 py-8">
        <aside className="w-56 shrink-0">
          <div className="sticky top-20 rounded-lg border border-line bg-card p-3">
            <nav className="flex flex-col gap-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = section === item.id;
                return (
                  <div key={item.id}>
                    <NavButton
                      id={`nav.${item.id}`}
                      label={item.label}
                      level={0}
                      active={isActive && !sub}
                      trace={isActive && !!sub}
                      onClick={() => {
                        setSection(item.id);
                        setSub(subsections[item.id]?.[0]?.id ?? null);
                      }}
                      icon={<Icon className="size-4 shrink-0" />}
                    />
                    {isActive && subs.length > 0 && (
                      <div className="mt-1 ml-6 flex flex-col gap-1 border-l border-line pl-2">
                        {subs.map((s) => (
                          <NavButton
                            key={s.id}
                            id={`nav.${item.id}.${s.id}`}
                            label={s.label}
                            level={1}
                            active={sub === s.id}
                            trace={false}
                            onClick={() => setSub(s.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col gap-6">
          <Block id="block.intro" label="О Наблюдателе">
            <h1 className="type-title text-fg">Хранитель этого пространства</h1>
            <p className="mt-2 max-w-2xl text-sm text-fg-muted">
              Он показывает, где ты находишься, чем занята система и что где появилось. Живая подложка всегда
              одна и стоит на самом глубоком активном уровне, верхний держит след. На событие он подлетает к
              источнику, обводит его кольцом, делает жест и возвращается домой.
            </p>
            {answer && (
              <div className="mt-4 animate-fade-in rounded-md border border-ai-line bg-ai-soft px-3 py-2 text-xs text-ai-strong">
                {answer}
              </div>
            )}
          </Block>

          <Block id="block.events" label="События">
            <h2 className="type-heading text-fg">События</h2>
            <p className="mt-1 text-sm text-fg-muted">
              Нажмите и смотрите: он отзывается жестом, а если источник известен, летит к нему и показывает.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {events.map((e) => (
                <Button
                  key={e.label}
                  size="sm"
                  variant="secondary"
                  onClick={() => notify({ kind: e.kind, anchorId: e.anchorId })}
                >
                  {e.label}
                </Button>
              ))}
            </div>
            <div
              ref={rowRef}
              className="mt-4 flex items-center gap-3 rounded-md border border-line bg-canvas px-3 py-2"
            >
              <span className="text-xs font-medium text-fg">Отчёт за неделю</span>
              <span className="flex-1 text-xs text-fg-subtle">Документ · изменён 5 минут назад</span>
              <Button ref={saveRef} size="sm" variant="secondary">
                <Save className="size-3.5" />
                Сохранить
              </Button>
            </div>
          </Block>

          <Block id="block.settings" label="Настройки Наблюдателя">
            <h2 className="type-heading text-fg">Его настройки</h2>
            <p className="mt-1 text-sm text-fg-muted">
              Всё, вплоть до полного отключения. Настройки сохраняются в браузере.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Switch
                size="sm"
                checked={settings.enabled}
                onCheckedChange={(v) => setSettings({ enabled: v })}
                label="Включён"
              />
              <Switch
                size="sm"
                checked={settings.followCursor}
                onCheckedChange={(v) => setSettings({ followCursor: v })}
                label="Следит за курсором"
              />
              <Switch
                size="sm"
                checked={settings.guidance}
                onCheckedChange={(v) => setSettings({ guidance: v })}
                label="Показывает, где появилось"
              />
              <Switch
                size="sm"
                checked={settings.calm}
                onCheckedChange={(v) => setSettings({ calm: v })}
                label="Не беспокоить"
              />
              <Switch
                size="sm"
                checked={settings.interactive}
                onCheckedChange={(v) => setSettings({ interactive: v })}
                label="Отвечает на нажатие"
              />
            </div>

            <div className="mt-5 flex flex-col gap-4">
              <Row label="Характер">
                {presences.map((p) => (
                  <Button
                    key={p.value}
                    size="sm"
                    variant={settings.presence === p.value ? 'primary' : 'secondary'}
                    onClick={() => setSettings({ presence: p.value })}
                  >
                    {p.label}
                  </Button>
                ))}
              </Row>
              <Row label="Сила движения">
                {motions.map((m) => (
                  <Button
                    key={m.value}
                    size="sm"
                    variant={settings.motion === m.value ? 'primary' : 'secondary'}
                    onClick={() => setSettings({ motion: m.value })}
                  >
                    {m.label}
                  </Button>
                ))}
              </Row>
              <Row label="Показывать события">
                {(Object.keys(eventNames) as ObserverEventKind[]).map((kind) => (
                  <Button
                    key={kind}
                    size="sm"
                    variant={settings.reactions[kind] ? 'primary' : 'secondary'}
                    onClick={() => toggleReaction(kind)}
                  >
                    {eventNames[kind]}
                  </Button>
                ))}
              </Row>
            </div>
          </Block>

          <Block id="block.manual" label="Состояние и форма">
            <h2 className="type-heading text-fg">Состояние и форма вручную</h2>
            <div className="mt-4 flex flex-col gap-4">
              <Row label="Состояние">
                {states.map((s) => (
                  <Button
                    key={s.value}
                    size="sm"
                    variant={state === s.value ? 'primary' : 'secondary'}
                    onClick={() => setState(s.value)}
                  >
                    {s.label}
                  </Button>
                ))}
              </Row>
              <Row label="Форма">
                {shapes.map((s) => (
                  <Button
                    key={s.value}
                    size="sm"
                    variant={shape === s.value ? 'primary' : 'secondary'}
                    onClick={() => setShape(s.value)}
                  >
                    {s.label}
                  </Button>
                ))}
              </Row>
            </div>
          </Block>

          <Block id="block.scroll" label="Присутствие при прокрутке">
            <h2 className="type-heading text-fg">Присутствие при прокрутке</h2>
            <p className="mt-1 text-sm text-fg-muted">
              Пролистайте вниз: он держится за свой якорь, гаснет, когда тот уходит с экрана, и появляется
              снова при возвращении.
            </p>
            <div className="mt-4 flex flex-col gap-3">
              {Array.from({ length: 12 }, (_, i) => (
                <div
                  key={i}
                  className="rounded-md border border-line bg-canvas px-4 py-3 text-sm text-fg-muted"
                >
                  Строка списка {i + 1}
                </div>
              ))}
            </div>
          </Block>
        </div>
      </main>
    </div>
  );
}

export function ObserverPlayground() {
  return (
    <ObserverProvider>
      <Playground />
      <Observer />
    </ObserverProvider>
  );
}
